import { Client, contractAddress } from "@verified-network/verified-sdk";
import axios from "axios";
import config from "../../services/config/homestead.json";
import {
  biconomyPaymasterEndpoint,
  biconomyWhitelistEndpoint,
  defaultVerifiedUrl,
  emailSenderUrl,
  networks,
  smsSenderUrl,
  verifiedGcexUrl,
  verifiedGTNBaseUrl,
} from "../constants";
import { NETWORKS_INFO } from "./networks";
import { ethers } from "ethers";
import ERC20ABI from "../abi/ERC20.json";
import CLIENTABI from "../abi/Client.json";
import { CID } from "multiformats/cid";
import { base58btc } from "multiformats/bases/base58";
import { create } from "multiformats/hashes/digest";

export const BUNDLE_ID = "1";

export const timeframeOptions = {
  FOUR_HOURS: "4 hours",
  ONE_DAY: "1 day",
  THERE_DAYS: "3 days",
  WEEK: "1 week",
  MONTH: "1 month",
  // THREE_MONTHS: '3 months',
  // YEAR: '1 year',
  ALL_TIME: "All time",
};

export const Network = {
  MAINNET: 1,
  ROPSTEN: 3,
  RINKEBY: 4,
  GÖRLI: 5,
  KOVAN: 42,
  POLYGON: 137,
  ARBITRUM: 42161,
};

export const getKycStatus = async (address, chainId, web3) => {
  const clientAddress = contractAddress[chainId].Client;
  const clientContract = new web3.eth.Contract(CLIENTABI, clientAddress);
  const kycDataResponse = await clientContract.methods
    .getClientKYC(address)
    .call({ from: address });
  const result = kycDataResponse;
  return Number(result[3]);
};

//Todo: add more login to handle token name to accepted token ticker on coinbase
export const getTokenTicker = (tokenName) => {
  if (tokenName.toLowerCase().startsWith("w")) {
    return tokenName.slice(1, tokenName.length);
  } else {
    return tokenName;
  }
};

export const gcexCreateSpotOrder = async (
  security,
  cash,
  type,
  amount,
  paymentRef,
  chainId,
) => {
  return await axios
    .post("https://gateway.verified.network/api/gcex/orders", {
      security,
      cash,
      type,
      amount,
      paymentRef,
      chainId,
    })
    .then((res) => {
      if (res?.data?.body?.order_status === "NEW") {
        return true;
      } else {
        return false;
      }
    })
    .catch((err) => {
      if (process.env.REACT_APP_NODE_ENV !== "production") {
        console.error("create spot trade failed: ", err?.message);
      }

      return false;
    });
};

export const laserCreateSpotOrder = async (
  security,
  cash,
  type,
  amount,
  paymentRef,
  price,
  chainId,
) => {
  return await axios
    .post("https://gateway.verified.network/api/laser/order", {
      security,
      cash,
      type: "IOC",
      amount,
      paymentRef,
      side: type,
      price,
    })
    .then((res) => {
      if (res?.data?.body) {
        return true;
      } else {
        return false;
      }
    })
    .catch((err) => {
      if (process.env.REACT_APP_NODE_ENV !== "production") {
        console.error("create spot order failed: ", err?.message);
      }

      return false;
    });
};

export const blinkCreateOrder = async (userId, providerId, orderBody) => {
  return await axios
    .post("https://gateway.verified.network/api/blink/create-order", {
      user_id: userId,
      provider_id: providerId,
      orderBody,
    })
    .then((res) => {
      if (res?.data?.body?.data) {
        console.log("create order return: ", res?.data?.body?.data);
        return res?.data?.body?.data;
      } else {
        return null;
      }
    })
    .catch((err) => {
      console.error("blink create order failed with error: ", err?.message);
      return null;
    });
};

export const getAllVerifiedCashBalances = async (
  chainId,
  web3,
  userAddress,
) => {
  const cashTokens = contractAddress[chainId]?.CASH;
  if (cashTokens && Object.values(cashTokens)?.length > 0) {
    const verifiedCashFmt = await Promise.all(
      Object.keys(cashTokens)?.map(async (tkNm) => {
        if (cashTokens[tkNm]?.length > 0) {
          const cashTokenContract = new web3.eth.Contract(
            ERC20ABI,
            cashTokens[tkNm],
          );
          const balance = await cashTokenContract.methods
            .balanceOf(userAddress)
            .call();
          return {
            name: tkNm,
            address: cashTokens[tkNm],
            balance: Number(balance) > 0 ? Number(balance) : 0,
            balanceFmt:
              Number(balance) > 0
                ? Number(ethers.utils.formatEther(Number(balance)))
                : 0,
          };
        } else {
          return null;
        }
      }),
    );

    const validverifiedCash = verifiedCashFmt?.filter((csh) => csh !== null);

    const validverifiedCashFmt = validverifiedCash?.sort(
      (a, b) => b.balance - a.balance,
    );

    if (
      (validverifiedCashFmt[0]?.balance === 0 &&
        NETWORKS_INFO[chainId]?.usdc?.length > 0) ||
      validverifiedCashFmt?.length === 0
    ) {
      const usdcContract = new web3.eth.Contract(
        ERC20ABI,
        NETWORKS_INFO[chainId]?.usdc,
      );
      const balance = await usdcContract.methods.balanceOf(userAddress).call();

      if (validverifiedCashFmt?.length > 0) {
        validverifiedCashFmt[1] = validverifiedCashFmt[0]; // go 1 back to keep the prev highest after USDC???
      }

      validverifiedCashFmt[0] = {
        name: "USDC",
        address: NETWORKS_INFO[chainId]?.usdc,
        balance: Number(balance) > 0 ? Number(balance) : 0,
        balanceFmt:
          Number(balance) > 0
            ? Number(ethers.utils.formatUnits(Number(balance), "6"))
            : 0,
      };
    }

    return validverifiedCashFmt;
  } else if (NETWORKS_INFO[chainId]?.usdc && web3) {
    const usdcContract = new web3.eth.Contract(
      ERC20ABI,
      NETWORKS_INFO[chainId]?.usdc,
    );
    const balance = await usdcContract.methods.balanceOf(userAddress).call();

    return [
      {
        name: "USDC",
        address: NETWORKS_INFO[chainId]?.usdc,
        balance: Number(balance) > 0 ? Number(balance) : 0,
        balanceFmt:
          Number(balance) > 0
            ? Number(ethers.utils.formatUnits(Number(balance), "6"))
            : 0,
      },
    ];
  } else {
    return;
  }
};

export const fetchAllNSDQAssets = async (page) => {
  return await axios({
    method: "GET",
    url: page
      ? `${verifiedGTNBaseUrl}/nsdq?rows=200&page=${page}`
      : `${verifiedGTNBaseUrl}/nsdq?rows=200`,
  })
    .then((res) => {
      return res.data?.body?.data?.response;
    })
    .catch((err) => {
      console.error(
        "Error while fetching all GTN NSDQ assests :",
        err?.message,
      );
      return {};
    });
};

export const convertIpfsHashToBytes32 = (ipfsHash) => {
  const cid = CID.parse(ipfsHash);
  const digest = cid.multihash.digest;
  if (digest.length !== 32) return null;
  return `0x${Buffer.from(digest)?.toString("hex")}`;
};

export const convertBytes32ToIpfsHash = (bytes32) => {
  const hex = bytes32.replace(/^0x/, "");
  const digestBytes = Uint8Array.from(Buffer.from(hex, "hex"));
  const multihash = create(0x12, digestBytes);
  const cid = CID.createV0(multihash);
  return cid.toString(base58btc);
};
